import {
  Box,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  Chip,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import OpenSidebar from '@assets/flowchart/OpenSidebar.svg'
import ArrowBack from '@assets/flowchart/ArrowBack.svg'
import EditIcon from '@assets/flowchart/EditIcon.svg'
import { useNavigate, useParams } from 'react-router-dom'
import { useEffect, useRef, useState, type Ref } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAppTheme } from '@/theme/ThemeContext'
import {
  createAlertType,
  getAlertType,
  getTemplateVariables,
  saveTree,
  updateAlertTypeText,
} from '@/services/notificationService'
import { useFlowState } from './FlowStateContext'
import { useReference } from './ReferenceContext'
import { channelIdsByKey } from './referenceOptions'
import { sameTree, toSchemaTree } from '@/utils/treeSerializer'
import { severityHintFromTree } from '@/utils/severity'
import { describeSaveError } from '@/alerts/saveError'
import { checkCompleteness, checkTreeCalendar } from '@/alerts/completeness'
import { nodeIdAtInstancePath, validateTree } from '@/alerts/validateTree'
import { describeCalendar } from '@/alerts/calendar'
import CalendarDialog from './calendar/CalendarDialog'

type Feedback = { severity: 'warning' | 'error'; message: string }

const Header = ({
  openSidebar,
  setOpenSidebar,
  ref,
}: {
  openSidebar: boolean
  setOpenSidebar: (openSidebar: boolean) => void
  ref?: Ref<HTMLDivElement>
}) => {
  const navigate = useNavigate()
  const [editHeaderName, setEditHeaderName] = useState(false)
  const [headerName, setHeaderName] = useState('התראה חדשה')
  const [input, setInput] = useState('')
  const [saving, setSaving] = useState(false)
  const [feedback, setFeedback] = useState<Feedback | null>(null)
  const { notificationId, siteId } = useParams()
  const flow = useFlowState()
  const reference = useReference()

  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const [calendarOpen, setCalendarOpen] = useState(false)
  const calendarInvalid = flow.treeCalendarIssues.length > 0

  // Allowed {{variable}} names for the message-body completeness check.
  // Same guarded localStorage convention as the send-dialog entry points
  // (Consumers.tsx) — and the same query key as MessageComposer's palette, so
  // react-query serves both from one fetch.
  const accountIdRaw = localStorage.getItem('accountId')
  const accountId = accountIdRaw ? Number(accountIdRaw) : NaN
  const { data: templateVariables = [], isPending: variablesPending } =
    useQuery({
      queryKey: ['template-variables', accountId],
      queryFn: () => getTemplateVariables(accountId),
      staleTime: 5 * 60 * 1000,
    })

  const colors = {
    text: isDarkMode ? '#FFFFFF' : '#003F61',
    iconFilter: isDarkMode ? 'brightness(0) invert(1)' : 'none',
    inputBg: isDarkMode ? '#202B40' : '#FAFBFD',
    inputBorder: isDarkMode ? '#374151' : 'rgba(50, 59, 119, 0.10)',
    chipBg: isDarkMode ? '#374151' : '#E3F5FF',
    invalid: isDarkMode ? '#F44336' : '#D32F2F',
  }

  // Hydrate header name + description from API for existing alerts. A field the author already
  // edited keeps the author's text, and Save waits for the type so it cannot write the defaults.
  const edited = useRef({ name: false, description: false })
  const [loadingType, setLoadingType] = useState(false)
  useEffect(() => {
    edited.current = { name: false, description: false }
    if (!siteId || !notificationId || notificationId === 'new') {
      setHeaderName('התראה חדשה')
      setInput('')
      setLoadingType(false)
      return
    }
    setLoadingType(true)
    let current = true
    getAlertType(siteId, Number(notificationId))
      .then(d => {
        if (!current) return
        if (!edited.current.name) setHeaderName(d.alertTypeName ?? 'התראה')
        if (!edited.current.description) setInput(d.description ?? '')
      })
      .catch(err => console.error('[Header] Failed to load alert type:', err))
      .finally(() => {
        if (current) setLoadingType(false)
      })
    return () => {
      current = false
    }
  }, [notificationId, siteId])

  const handleSave = async () => {
    if (!siteId || !headerName) return

    // A save while the template-variables fetch is still in flight would run
    // the token check against the empty default list and false-block valid
    // tokens as unknown — block with a loading message instead.
    if (variablesPending) {
      setFeedback({
        severity: 'warning',
        message: 'רשימת המשתנים עדיין נטענת — נסו לשמור שוב בעוד רגע',
      })
      return
    }

    // Layer A — completeness (friendly, blocks incomplete input before we
    // serialize). Highlights the offending nodes on the canvas.
    const issues = checkCompleteness(
      flow.nodes,
      flow.edges,
      templateVariables.map(v => v.variable),
      flow.rootId,
      new Set(
        reference.attributes
          .filter(a => a.valueEntry === 'consumers')
          .map(a => a.name)
      ),
      flow.treeCalendar,
      reference.notificationChannels.map(c => ({
        id: String(c.notificationChannelId),
        key: c.notificationChannelKey,
        category: c.category,
        name: c.name,
      }))
    )
    const treeIssues = checkTreeCalendar(
      flow.treeCalendar,
      flow.nodes,
      flow.edges,
      flow.rootId
    )
    flow.setTreeCalendarIssues(treeIssues)
    if (issues.length > 0 || treeIssues.length > 0) {
      flow.setInvalidNodeIds(issues.map(i => i.nodeId))
      const messages = Array.from(
        new Set([...treeIssues, ...issues.map(i => i.message)])
      )
      setFeedback({
        severity: 'warning',
        message: messages.slice(0, 4).join(' • '),
      })
      return
    }
    flow.setInvalidNodeIds([])

    setSaving(true)
    try {
      const tree = toSchemaTree(
        flow.nodes,
        flow.edges,
        flow.rootId,
        flow.viewport,
        flow.loaded?.kept,
        flow.treeCalendar,
        channelIdsByKey(reference)
      )

      // Layer B — schema validation (parity with the gateway/compiler). Should
      // effectively never fire; if it does, our serializer/types have a bug, so
      // we block the save and log loudly rather than ship invalid JSON.
      const result = validateTree(tree)
      if (!result.valid) {
        for (const err of result.errors) {
          const nodeId = nodeIdAtInstancePath(tree, err.instancePath)
          console.error(
            `[Header] Layer-B schema violation at ${err.instancePath || '$'}` +
              ` (node ${nodeId ?? '?'}): ${err.message}`,
            err.params
          )
        }
        console.error('[Header] Invalid tree blocked from save:', {
          siteId,
          alertTypeId: notificationId,
          tree,
        })
        setFeedback({
          severity: 'error',
          message: 'שמירה נכשלה — אירעה שגיאה בלתי צפויה, הפרטים נשמרו ליומן',
        })
        setSaving(false)
        return
      }

      const severityHint = severityHintFromTree(tree)
      if (notificationId === 'new' || !notificationId) {
        // The Gateway publishes the tree in the same call, so a refused tree leaves no alert type behind.
        await createAlertType(siteId, {
          alertTypeName: headerName,
          description: input,
          severityHint,
          initialTree: { tree },
        })
      } else {
        const typeId = Number(notificationId)
        await updateAlertTypeText(siteId, typeId, {
          alertTypeName: headerName,
          description: input,
          severityHint,
        })
        // Publishing the same tree again would reopen the opening messages of every open alert of the type.
        const unchanged =
          !!flow.loaded?.baseline && sameTree(tree, flow.loaded.baseline)
        if (!unchanged) await saveTree(siteId, typeId, tree)
      }
      navigate(`/${siteId}/notifications/manage-notifications`)
    } catch (err) {
      console.error('[Header] Save failed:', err)
      setFeedback({ severity: 'error', message: describeSaveError(err) })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Box
      ref={ref}
      sx={{
        paddingTop: '60px',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        height: 'fit-content',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{ cursor: 'pointer' }}
          onClick={() => setOpenSidebar(!openSidebar)}
        >
          {!openSidebar && (
            <img
              style={{ marginLeft: '30px', filter: colors.iconFilter }}
              src={OpenSidebar}
              alt="open sidebar-header"
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <img
              onClick={() => {
                if (headerName) setEditHeaderName(!editHeaderName)
              }}
              style={{ marginTop: '14px', filter: colors.iconFilter }}
              src={EditIcon}
              alt="edit icon"
            />
            {editHeaderName ? (
              <TextField
                id="standard-basic"
                label="כותרת"
                variant="standard"
                sx={{ width: '300px' }}
                error={!headerName}
                value={headerName}
                dir="rtl"
                onChange={e => {
                  edited.current.name = true
                  setHeaderName(e.target.value)
                }}
              />
            ) : (
              <Typography
                sx={{
                  color: colors.text,
                  textAlign: 'right',
                  fontFamily: 'Rubik, sans-serif',
                  fontSize: '50px',
                  fontWeight: 700,
                  lineHeight: '38px',
                }}
              >
                {headerName}
              </Typography>
            )}
          </Box>

          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={saving || loadingType || !headerName}
            onClick={handleSave}
            sx={{
              fontFamily: 'Rubik, sans-serif',
              bgcolor: '#72A4EB',
              borderRadius: '6px',
              textTransform: 'none',
              '&:hover': { bgcolor: '#5A8FD4' },
            }}
          >
            {saving ? 'שומר…' : 'שמור'}
          </Button>

          <img
            onClick={() =>
              navigate(`/${siteId}/notifications/manage-notifications`)
            }
            src={ArrowBack}
            alt="arrow back"
            style={{ filter: colors.iconFilter, cursor: 'pointer' }}
          />
        </Box>
      </Box>
      <Box textAlign="right">
        <textarea
          value={input}
          onChange={e => {
            edited.current.description = true
            setInput(e.target.value)
          }}
          dir="rtl"
          style={{
            marginTop: '28px',
            width: '400px',
            maxWidth: '400px',
            height: '50px',
            maxHeight: '100px',
            minHeight: '50px',
            color: colors.text,
            borderRadius: '8px',
            border: `1px solid ${colors.inputBorder}`,
            background: colors.inputBg,
          }}
          placeholder="כתיבת תיאור"
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 1 }}>
        <Chip
          dir="rtl"
          label={`לוח זמנים: ${flow.treeCalendar ? describeCalendar(flow.treeCalendar) : 'תמיד'}`}
          onClick={() => setCalendarOpen(true)}
          aria-haspopup="dialog"
          data-invalid={calendarInvalid}
          sx={{
            fontFamily: 'Rubik, sans-serif',
            maxWidth: '100%',
            color: calendarInvalid ? colors.invalid : colors.text,
            backgroundColor: colors.chipBg,
            border: `1px solid ${calendarInvalid ? colors.invalid : 'transparent'}`,
          }}
        />
      </Box>
      <CalendarDialog
        open={calendarOpen}
        title="לוח זמנים לעץ"
        note="חל על כל בדיקת צריכה שאין לה לוח משלה."
        grain="hour"
        value={flow.treeCalendar}
        issues={flow.treeCalendarIssues}
        onChange={flow.setTreeCalendar}
        onRemove={() => flow.setTreeCalendar(undefined)}
        onClose={() => setCalendarOpen(false)}
      />

      <Snackbar
        open={!!feedback}
        autoHideDuration={feedback?.severity === 'error' ? null : 6000}
        onClose={() => setFeedback(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {feedback ? (
          <Alert
            severity={feedback.severity}
            onClose={() => setFeedback(null)}
            variant="filled"
            sx={{ fontFamily: 'Rubik, sans-serif' }}
            dir="rtl"
          >
            {feedback.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  )
}

export default Header
